import { useEffect, useRef } from "react";
import { useMap } from "react-map-gl";
import { useDispatch, useSelector } from "react-redux";
import { useMapboxDraw } from "../..";
import { inputsActions, canopyActions } from "../..";

const HandleFeatureClick = ({ features }) => {
  const dispatch = useDispatch();
  const { current: mapRef } = useMap();
  const map = mapRef.getMap();
  const draw = useMapboxDraw();

  const selectedFeatureId = useSelector((state) => state.inputs.selectedFeatureId);
  const selectedFeatureId_ref = useRef(selectedFeatureId);
  const selected_shade_object_id = useSelector((state) => state.canopy.selected_shade_object_id);

  // Refs for multi-selection highlight
  const highlightFillLayerId = "multi-select-highlight-fill";
  const highlightSourceId = "multi-select-highlight-source";

  const drawing_building = useSelector((state) => state.canopy.drawing_building);
  const drawing_tree = useSelector((state) => state.canopy.drawing_tree);
  const drawing_tree_line = useSelector((state) => state.canopy.drawing_tree_line);
  const is_drawing = useSelector((state) => state.inputs.uiState.is_drawing);
  const drawing_is_happening = drawing_building || drawing_tree || drawing_tree_line || is_drawing;

  const drawing_is_happening_ref = useRef(drawing_is_happening);

  const is_measuring = useSelector((state) => state.inputs.uiState.is_measuring);
  const is_measuring_ref = useRef(is_measuring);

  useEffect(() => {
    is_measuring_ref.current = is_measuring;
  }, [is_measuring]);

  useEffect(() => {
    drawing_is_happening_ref.current = drawing_is_happening;
  }, [drawing_is_happening]);

  useEffect(() => {
    selectedFeatureId_ref.current = selectedFeatureId;

    // Update highlight when selectedFeatureId changes
    if (!map || !draw) return;

    console.log("selectedFeatureId changed:", selectedFeatureId);

    if (Array.isArray(selectedFeatureId)) {
      if (selectedFeatureId.length > 0) {
        // console.log("Array features selected, showing highlight");

        const selectedFeatures = selectedFeatureId
          .map((id) => {
            const feature = draw.get(id);
            console.log("Getting feature with id:", id, "result:", feature);
            return feature;
          })
          .filter(Boolean);

        // console.log("Total selected features found:", selectedFeatures.length);

        if (selectedFeatures.length > 0) {
          const source = map.getSource(highlightSourceId);

          if (source) {
            const featureCollection = {
              type: "FeatureCollection",
              features: selectedFeatures,
            };
            source.setData(featureCollection);
          } else {
            // console.error("Highlight source not found!");
          }
        }
      } else {
        // Empty array - clear highlight
        const source = map.getSource(highlightSourceId);
        if (source) {
          source.setData({
            type: "FeatureCollection",
            features: [],
          });
        }
      }
    } else {
      // Not an array - clear the highlight
      const source = map.getSource(highlightSourceId);
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: [],
        });
      }
    }
  }, [selectedFeatureId, map, draw]);

  useEffect(() => {
    if (!map || !draw) return;

    const initializeLayers = () => {
      // Initialize highlight layers for multi-selection
      if (!map.getSource(highlightSourceId)) {
        map.addSource(highlightSourceId, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });
      }

      // Get all layer IDs to find the topmost draw layer
      const layers = map.getStyle().layers;
      let topDrawLayerId = null;

      // Find the last (topmost) gl-draw layer
      for (let i = layers.length - 1; i >= 0; i--) {
        if (layers[i].id.includes("gl-draw") || layers[i].id.includes("mapbox-gl-draw")) {
          topDrawLayerId = layers[i].id;
          break;
        }
      }

      // Add fill layer for multi-selection indicator
      if (!map.getLayer(highlightFillLayerId)) {
        map.addLayer({
          id: highlightFillLayerId,
          type: "fill",
          source: highlightSourceId,
          paint: {
            "fill-color": "#87CEEB",
            "fill-opacity": 0.5,
          },
        });

        // Verify layer was added
      }

      // Move layer to the very top (no beforeId means move to top)
      try {
        if (map.getLayer(highlightFillLayerId)) {
          map.moveLayer(highlightFillLayerId);
        }
      } catch (e) {
        // console.warn("Could not move layer:", e);
      }
    };

    // Wait for map to be loaded before adding layers
    if (map.loaded()) {
      initializeLayers();
    } else {
      map.on("load", initializeLayers);
    }

    map.on("click", handleClick);
    map.on("dblclick", handleDoubleClick);

    return () => {
      map.off("click", handleClick);
      map.off("dblclick", handleDoubleClick);
    };
  }, [map, features]);

  const handleDoubleClick = (e) => {
    // don't interfere with measuring or active drawing
    if (is_measuring_ref.current) return;
    if (drawing_is_happening_ref.current) return;

    const featureIds = draw.getFeatureIdsAt(e.point);
    if (!featureIds || featureIds.length === 0) return;

    const clickedFeatures = featureIds.map((id) => draw.get(id)).filter(Boolean);
    if (clickedFeatures.length === 0) return;

    const getShape = (f) => (f && f.properties && f.properties.shape ? String(f.properties.shape).toLowerCase() : "");

    // pick top-most/next feature similar to single click logic
    const isModifier = e && e.originalEvent && (e.originalEvent.shiftKey || e.originalEvent.altKey || e.originalEvent.ctrlKey || e.originalEvent.metaKey);
    let featureToEdit = clickedFeatures[0];
    if (clickedFeatures.length > 1) {
      const currentIndex = clickedFeatures.findIndex((f) => {
        const shape = getShape(f);
        if (shape === "sitefeature") {
          return selectedFeatureId_ref.current != null && f.properties.index === selectedFeatureId_ref.current;
        }
        if (["tree", "building", "treeline"].indexOf(shape) !== -1) {
          return selected_shade_object_id && f.id === selected_shade_object_id;
        }
        return false;
      });

      if (currentIndex >= 0) {
        featureToEdit = clickedFeatures[(currentIndex + 1) % clickedFeatures.length];
      } else if (isModifier) {
        featureToEdit = clickedFeatures[1];
      }
    }

    if (!featureToEdit) return;

    const shape = getShape(featureToEdit);

    // We only handle polygon and linestring vertex removal here which are the site features and the treelines
    const geomType = featureToEdit.geometry && featureToEdit.geometry.type;
    if (!geomType) return;

    let coords = null;
    let isPolygon = false;
    if (geomType === "Polygon") {
      coords = (featureToEdit.geometry.coordinates && featureToEdit.geometry.coordinates[0] && featureToEdit.geometry.coordinates[0].slice()) || null;
      isPolygon = true;
    } else if (geomType === "LineString") {
      coords = featureToEdit.geometry.coordinates && featureToEdit.geometry.coordinates.slice();
    } else {
      return;
    }

    if (!coords || coords.length === 0) return;

    // find nearest vertex to the click point
    const clickPoint = e.point; // this gets the pixel coords of where you clicked on the map
    let minDistSq = Infinity;
    let minIndex = -1;

    const searchLength = isPolygon ? coords.length - 1 : coords.length;

    for (let i = 0; i < searchLength; i++) {
      const coord = coords[i];
      const p = map.project(coord);
      const dx = p.x - clickPoint.x;
      const dy = p.y - clickPoint.y;
      const distSq = dx * dx + dy * dy;
      if (distSq < minDistSq) {
        minDistSq = distSq;
        minIndex = i;
      }
    }

    const PIXEL_THRESHOLD = 12; // px
    if (minIndex < 0 || minDistSq > PIXEL_THRESHOLD * PIXEL_THRESHOLD) return;

    // Ensure this handle removal won't invalidate the polygon geometry since a polygon needs >= 3 vertecies
    const uniqueVerticesBefore = isPolygon ? coords.length - 1 : coords.length;
    const uniqueVerticesAfter = uniqueVerticesBefore - 1;
    if (isPolygon && uniqueVerticesAfter < 3) {
      // can't remove it, get outta here
      return;
    }
    if (!isPolygon && uniqueVerticesAfter < 2) {
      // a line would need at least 2 points, so get outta here
      return;
    }

    coords.splice(minIndex, 1);

    // restore polygon closing coordinate if we need to
    let newCoordinates;
    if (isPolygon) {
      if (coords.length === 0) return;
      const ring = coords.slice();
      ring[ring.length - 1] = ring[0];
      newCoordinates = [ring];
    } else {
      newCoordinates = coords;
    }

    // build the new feature
    const newFeature = JSON.parse(JSON.stringify(featureToEdit));
    newFeature.geometry = {
      ...newFeature.geometry,
      coordinates: newCoordinates,
    };

    // preserve id so selection and other code are GTG
    const originalId = featureToEdit.id;
    if (originalId) newFeature.id = originalId;
    // If siteFeature, ensure id matches properties.index
    if (newFeature.properties && newFeature.properties.index != null) newFeature.id = newFeature.properties.index;

    // Dispatch update similar to draw.update remove_vertex handling so the app state stays synced up
    try {
      if (shape === "sitefeature") {
        dispatch(inputsActions.update_site_input("edit_site_feature", newFeature));
      }
    } catch (err) {
      // console.warn("Error dispatching edit_site_feature on dblclick vertex removal:", err);
    }

    // Replace feature in draw store by deleting and re-adding (preserve id)
    try {
      // delete using id that was originally used to add the feature (feature.properties.index)
      const deleteId = featureToEdit.properties && featureToEdit.properties.index != null ? featureToEdit.properties.index : featureToEdit.id;
      draw.delete(deleteId);
      draw.add(newFeature);
      // reselect the updated feature
      selectFeatureOnMap(newFeature.id);
    } catch (err) {
      // console.warn("Error updating feature vertices on dblclick:", err);
    }
  };

  const showMultiSelectionHighlight = (featureIds) => {
    // Get the features and create highlight geometry
    const selectedFeatures = featureIds
      .map((id) => {
        const feature = draw.get(id);
        // console.log("Getting feature with id:", id, "result:", feature);
        return feature;
      })
      .filter(Boolean);

    // console.log("Total selected features found:", selectedFeatures.length);

    if (selectedFeatures.length > 0) {
      const source = map.getSource(highlightSourceId);

      if (source) {
        const featureCollection = {
          type: "FeatureCollection",
          features: selectedFeatures,
        };
        source.setData(featureCollection);
      } else {
        // console.error("Highlight source not found!");
      }
    } else {
      // console.warn("No features found to highlight");
    }
  };

  const clearMultiSelectionHighlight = () => {
    if (map.getSource(highlightSourceId)) {
      map.getSource(highlightSourceId).setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  };

  const selectFeatureOnMap = (id) => {
    if (!id) return;
    // console.log(id, "is the id of the feature we want to select");
    // Handle both single ID and array of IDs
    if (Array.isArray(id)) {
      if (id.length === 0) return;
      // For arrays, deselect in draw and only show custom highlight (no handles)
      draw.changeMode("simple_select");
      showMultiSelectionHighlight(id);
      // console.log("selecting multiple features from array with ids:", id);
    } else {
      // Single ID - show vertices for editing
      clearMultiSelectionHighlight();
      draw.changeMode("direct_select", { featureId: id });
    }
  };

  const handleClick = (e) => {
    if (is_measuring_ref.current) return;

    const featureIds = draw.getFeatureIdsAt(e.point);

    if (!featureIds || featureIds.length === 0) {
      // have to check for null to prevent this from running when a poly is created
      if (!drawing_is_happening_ref.current) {
        if (selectedFeatureId_ref.current) dispatch(inputsActions.update_site_input("selectedFeatureId", undefined));
        if (selected_shade_object_id) dispatch(canopyActions.updateShadeObject("selected_shade_object_id", undefined));
      }
      return;
    }

    // Map ids to feature objects and filter out the nulls
    const clickedFeatures = featureIds.map((id) => draw.get(id)).filter(Boolean);

    if (clickedFeatures.length === 0) return;

    // This just makes the shape comparison easier
    const getShape = (f) => (f && f.properties && f.properties.shape ? String(f.properties.shape).toLowerCase() : "");

    // Check for multi-select modifier key (Ctrl on Windows, Command on Mac)
    const isMultiSelectModifier = e && e.originalEvent && (e.originalEvent.ctrlKey || e.originalEvent.metaKey);

    // If we have multiple overlapping features, we cycle through them.
    // Set up some hot keys here--if user holds Shift/Alt, select the next feature under the top; otherwise,
    // if the top feature is already selected, a normal click will select the next one under it.
    const isModifier = e && e.originalEvent && (e.originalEvent.shiftKey || e.originalEvent.altKey);

    let featureToSelect = clickedFeatures[0];

    if (clickedFeatures.length > 1) {
      // Convert current selection to array for easier checking
      const currentSelection = Array.isArray(selectedFeatureId_ref.current) ? selectedFeatureId_ref.current : selectedFeatureId_ref.current != null ? [selectedFeatureId_ref.current] : [];

      // figure out if one of the clicked features is currently selected
      const currentIndex = clickedFeatures.findIndex((f) => {
        const shape = getShape(f);
        if (shape === "sitefeature") {
          // Check if this feature is in the current selection (works for both single and multi-select)
          return currentSelection.includes(f.properties.index);
        }
        if (["tree", "building", "treeline"].indexOf(shape) !== -1) {
          return selected_shade_object_id && f.id === selected_shade_object_id;
        }
        return false;
      });

      // For multi-select mode with overlapping features, cycle through to select different features
      if (isMultiSelectModifier && currentIndex >= 0) {
        // When multi-selecting overlapping features, cycle to the next one
        featureToSelect = clickedFeatures[(currentIndex + 1) % clickedFeatures.length];
      } else if (currentIndex >= 0 && !isMultiSelectModifier) {
        // Normal mode: If currently selected is one of the stacked features, pick the next feature
        featureToSelect = clickedFeatures[(currentIndex + 1) % clickedFeatures.length];
      } else if (isModifier && !isMultiSelectModifier) {
        // If modifier is held and none of the stacked features are selected, pick the next one
        featureToSelect = clickedFeatures[1];
      }
    }

    if (!featureToSelect) return;

    const shape = getShape(featureToSelect);

    if (shape === "sitefeature") {
      const clickedId = featureToSelect.properties.index;

      // Handle multi-select with Ctrl/Command key
      if (isMultiSelectModifier) {
        let newSelection;

        // Convert current selection to array if needed
        const currentSelection = Array.isArray(selectedFeatureId_ref.current) ? selectedFeatureId_ref.current : selectedFeatureId_ref.current != null ? [selectedFeatureId_ref.current] : [];

        // Check if clicked feature is already selected
        if (currentSelection.includes(clickedId)) {
          // Remove from selection
          newSelection = currentSelection.filter((id) => id !== clickedId);

          // Keep as array to maintain multi-select mode, or undefined if empty
          if (newSelection.length === 0) {
            newSelection = undefined;
          }
          // Keep as array even with 1 item when using multi-select
        } else {
          // Add to selection - always keep as array
          newSelection = [...currentSelection, clickedId];
        }

        dispatch(inputsActions.update_site_input("selectedFeatureId", newSelection));
        if (selected_shade_object_id) dispatch(canopyActions.updateShadeObject("selected_shade_object_id", undefined));
        selectFeatureOnMap(newSelection);
      } else {
        // Normal single select
        dispatch(inputsActions.update_site_input("selectedFeatureId", clickedId));
        if (selected_shade_object_id) dispatch(canopyActions.updateShadeObject("selected_shade_object_id", undefined));
        selectFeatureOnMap(clickedId);
      }
    } else if (["tree", "building", "treeline"].indexOf(shape) !== -1) {
      dispatch(canopyActions.updateShadeObject("selected_shade_object_id", featureToSelect.id));
      if (selectedFeatureId_ref.current) dispatch(inputsActions.update_site_input("selectedFeatureId", undefined));
      selectFeatureOnMap(featureToSelect.id);
    }
  };

  return null;
};

export { HandleFeatureClick };
